import { apiClient } from './apiClient';

// Typed client for OZIRA Forward's real backend (server.js's `/api/forward/*`
// routes, backed by lib/forwardworkflow.js). A Forward plan's tasks drive
// real work already built elsewhere in OZIRA (Code executions, via the same
// execution gateway Code's own workspace uses) -- every shape here is read
// directly off that handler, nothing speculative.

export interface ForwardCapabilities {
  plans: boolean;
  runs: boolean;
  approvals: boolean;
  notifyEmail: { configured: boolean; reason: string | null };
  notifyTelegram: { configured: boolean; linked: boolean; reason: string | null };
  limits: { MAX_PLANS: number; MAX_TASKS_PER_PLAN: number };
}

export async function getForwardCapabilities(): Promise<ForwardCapabilities> {
  const { data } = await apiClient.call<ForwardCapabilities>('/api/forward/capabilities');
  return data;
}

export type ForwardTaskKind = 'code_execution' | 'notify_email' | 'notify_telegram' | 'approval';
export type ForwardExecutionKind = 'test' | 'build' | 'lint' | 'typecheck' | 'format' | 'deploy';

export interface ForwardTaskInput {
  kind: ForwardTaskKind;
  requiresApproval?: boolean;
  // code_execution
  projectId?: string;
  executionKind?: ForwardExecutionKind;
  taskId?: string;
  deploymentProvider?: string;
  // notify_email
  to?: string;
  subject?: string;
  body?: string;
  // approval
  label?: string;
}

export interface ForwardTask {
  id: string;
  order: number;
  kind: ForwardTaskKind;
  requiresApproval: boolean;
  projectId?: string | null;
  executionKind?: ForwardExecutionKind | null;
  taskId?: string | null;
  deploymentProvider?: string | null;
  to?: string;
  subject?: string;
  body?: string;
  label?: string;
}

export interface ForwardPlan {
  id: string;
  ownerId: string;
  name: string;
  tasks: ForwardTask[];
  createdAt: number;
  updatedAt: number;
}

export type ForwardStepStatus = 'pending' | 'running' | 'awaiting_approval' | 'completed' | 'failed';

export interface ForwardRunStep {
  taskId: string;
  kind: ForwardTaskKind;
  label: string | null;
  status: ForwardStepStatus;
  executionId: string | null;
  startedAt: number | null;
  finishedAt: number | null;
  result: { exitCode?: number | null; durationMs?: number | null; status?: string } | null;
  error: { code: string; message: string } | null;
  approved?: boolean;
}

export type ForwardRunStatus = 'running' | 'awaiting_approval' | 'completed' | 'failed' | 'cancelled';

export interface ForwardRun {
  id: string;
  planId: string;
  planName: string;
  ownerId: string;
  status: ForwardRunStatus;
  currentStepIndex: number;
  steps: ForwardRunStep[];
  createdAt: number;
  updatedAt: number;
}

export async function listForwardPlans(): Promise<ForwardPlan[]> {
  const { data } = await apiClient.call<{ plans: ForwardPlan[] }>('/api/forward/plans');
  return data.plans || [];
}

export async function createForwardPlan(input: { name: string; tasks: ForwardTaskInput[] }): Promise<ForwardPlan> {
  const { data } = await apiClient.call<{ plan: ForwardPlan }>('/api/forward/plans', { method: 'POST', body: input });
  return data.plan;
}

export async function getForwardPlan(planId: string): Promise<ForwardPlan> {
  const { data } = await apiClient.call<{ plan: ForwardPlan }>(`/api/forward/plans/${encodeURIComponent(planId)}`);
  return data.plan;
}

export async function updateForwardPlan(planId: string, input: { name?: string; tasks?: ForwardTaskInput[] }): Promise<ForwardPlan> {
  const { data } = await apiClient.call<{ plan: ForwardPlan }>(`/api/forward/plans/${encodeURIComponent(planId)}`, { method: 'PUT', body: input });
  return data.plan;
}

export async function deleteForwardPlan(planId: string): Promise<{ ok: boolean }> {
  const { data } = await apiClient.call<{ ok: boolean }>(`/api/forward/plans/${encodeURIComponent(planId)}`, { method: 'DELETE' });
  return data;
}

export async function runForwardPlan(planId: string): Promise<ForwardRun> {
  const { data } = await apiClient.call<{ run: ForwardRun }>(`/api/forward/plans/${encodeURIComponent(planId)}/run`, { method: 'POST', body: {} });
  return data.run;
}

export async function listForwardRuns(planId?: string): Promise<ForwardRun[]> {
  const suffix = planId ? `?plan=${encodeURIComponent(planId)}` : '';
  const { data } = await apiClient.call<{ runs: ForwardRun[] }>(`/api/forward/runs${suffix}`);
  return data.runs || [];
}

export async function getForwardRun(runId: string): Promise<ForwardRun> {
  const { data } = await apiClient.call<{ run: ForwardRun }>(`/api/forward/runs/${encodeURIComponent(runId)}`);
  return data.run;
}

export async function approveForwardRun(runId: string): Promise<ForwardRun> {
  const { data } = await apiClient.call<{ run: ForwardRun }>(`/api/forward/runs/${encodeURIComponent(runId)}/approve`, { method: 'POST', body: {} });
  return data.run;
}

export async function rejectForwardRun(runId: string, reason?: string): Promise<ForwardRun> {
  const { data } = await apiClient.call<{ run: ForwardRun }>(`/api/forward/runs/${encodeURIComponent(runId)}/reject`, { method: 'POST', body: { reason } });
  return data.run;
}

export async function cancelForwardRun(runId: string): Promise<ForwardRun> {
  const { data } = await apiClient.call<{ run: ForwardRun }>(`/api/forward/runs/${encodeURIComponent(runId)}/cancel`, { method: 'POST', body: {} });
  return data.run;
}
